import { Plegma } from '@API/Api';
import { PlegmaStatics } from '@API/PlegmaStatics';
import { WebNode } from '@API/WebNode';
import { StateStatus, WebThingKeys, WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
import { PollingSettings } from '@ModelViews/ModelViews';
import { AdvancedInfoTip, createPortStateFromInfoTipEvent, InfoTipItem } from '@Primitive/InfoTips';
import { Localization, LocalizationChangeLanguageReq } from '@Primitive/Localization/Localization';
import { eIconType } from '@Primitive/SettingsListType';
import { ToolbarIconInfo } from '@Primitive/ToolbarIconButton';
import { IconURIPath } from '@UI/Widgets/Things/WidgetsThingsStructs';
import { DebugEx } from "@Utils/DebugEx";
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { getUrlParameters, isDefined, isObject, RunSafe } from "@Utils/Micro";
import { addDocumentFocus, parseStrIntoStrArr, removeDocumentFocus, yHas } from '@Utils/Utils';
import _ from "lodash";
import { IButtonPressedFromAdvancedToolbarIcon, IElementChangedFromAdvancedToolbarIcon } from "../Dashboard/DashboardManager";
import { showToastNotification, ToastNotificationSchemeValidator } from "../Primitive/ToastNotification";
import { IWidgetThingsClass } from '../Widgets/Things/Utils/IWidgetThingsClass';
import { BoardMacroEngine } from "./BoardMacroEngine";
import { DashboardDescriptor, DashboardDescriptorDictionary } from './DashboardStructs';
// ================================================================================================
export interface BoardThingsInterface {
    refreshRequest: () => void;

    //Dialog mngmt
    openDialogDashboard: (popupPage: PopupPage) => void;
    closeDialogDashboard: (indexes: number[]) => void;

    // Page Management
    changeSelectedPage: (key: string) => void;
    hidePages: (pages: string[]) => void;
    unHidePages: (pages: string[]) => void;

    // Toolbar icons Management
    selectToolbarIcons: (icons: string[]) => void;
    hideToolbarIcons: (icons: string[]) => void;
    unhideToolbarIcons: (icons: string[]) => void;
    addLeftToolbarIcons: (icons: ToolbarIconInfo[]) => void;
    addRightToolbarIcons: (icons: ToolbarIconInfo[]) => void;
    setToolbarIcons: (data: ToolbarIcons) => void;
    onLanguageChange: (language: string) => void;

    //Drawer mngmt
    openDrawerPage: (drawerPage: DrawerPage) => void;
    closeDrawerPage: (position: string) => void;
}
// ------------------------------------------------------------------------------------------------
export interface ToolbarIcons {
    left: ToolbarIconInfo[];
    center: ToolbarIconInfo[];
    right: ToolbarIconInfo[];
}
// ------------------------------------------------------------------------------------------------
export const enum LocationStatus {
    Disabled = "disabled",
    Stopped = "stopped",
    NotSupported = "not-supported",
    Started = "started",
    Valid = "valid",
    Error = "error"
}
// ------------------------------------------------------------------------------------------------
export const enum BoardThingMgmtPortKeys {
    // Input
    GoTo = "GoTo",
    OpenPageDialog = "OpenPageDialog",
    ClosePageDialog = "ClosePageDialog",
    HidePage = "HidePage",
    UnhidePage = "UnhidePage",
    ToastNotification = "ToastNotification",
    OpenDrawerPage = "OpenDrawerPage",
    CloseDrawerPage = "CloseDrawerPage",
    SelectToolbarIcons = "SelectToolbarIcons",
    HideToolbarIcons = "HideToolbarIcons",
    UnhideToolbarIcons = "UnhideToolbarIcons",
    AddLeftToolbarIcons = "AddLeftToolbarIcons",
    AddRightToolbarIcons = "AddRightToolbarIcons",
    SetToolbarIcons = 'SetToolbarIcons',
    OpenURL = "OpenURL",
    RedirectURL = "RedirectURL",
    SetBoardLanguage = "SetBoardLanguage",
    CopyToClipboard = "CopyToClipboard",

    // Output
    ActiveDashboardKey = "ActiveDashboardKey",
    InitDashboardKey = "InitDashboardKey",
    ToastNotificationFeedback = "ToastNotificationFeedback",
    ToolbarIconsSelected = "ToolbarIconsSelected",
    ButtonPressedFromAdvancedToolbarIcon = "buttonpressedfromadvancedtoolbaricon",
    ElementChangedFromAdvancedToolbarIcon = "elementchangedfromadvancedtoolbaricon",
    BoardLanguageSelected = "BoardLanguageSelected"
}
// ------------------------------------------------------------------------------------------------
export interface DrawerPage {
    position: string;
    size: number;
    pageId: string;
    isModal: boolean;
    isStatic: boolean;
    insideBody: boolean;
    title: string;
    fontIcon: string;

    /*
     * Runtime open/closed state.
     * Set only by drawer parent.
     */
    open?: boolean;
}
// ----------------------------------------------------------------------------------------------------------
// validator for advanced tippy of toolbar icons
const advancedTippyValidator = {
    type: 'object',
    properties: {
        width: { type: 'number' },
        height: { type: 'number' },
        elements: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    name: { type: 'string' },
                    elements: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: ['number', 'string'] },
                                label: { type: 'string' },
                                dataPath: { type: 'string' }
                            }
                        }
                    }
                }
            }
        },
        data: { type: 'object' },
        style: { type: 'object' }
    }
};
// ----------------------------------------------------------------------------------------------------------
function ToolbarIconsSchemeValidator(): JsonSchemaValidator {
    return JsonSchemaValidator.GetInstance("ToolbarIconsSchemeValidator", {
        type: "array",
        items: {
            type: 'object',
            required: ["id"],
            properties: {
                // nested icons
                nestedItems: {
                    type: "array",
                    items: {
                        type: 'object',
                        required: ["id"],
                        properties: {
                            // BoardToolbarIcon properties
                            isSeparator: { type: "boolean" },
                            tooltip: { type: "string" },
                            id: { type: "string" },
                            icon: { type: "string" },
                            iconType: { type: "number", enum: [eIconType.fontAwesome, eIconType.url, eIconType.hosted] },
                            iconColor: { type: "string" },
                            autoSelect: { type: "boolean" }
                        }
                    }
                },
                // BoardToolbarIcon properties
                isSeparator: { type: "boolean" },
                tooltip: { type: "string" },
                id: { type: "string" },
                icon: { type: "string" },
                iconType: { type: "number", enum: [eIconType.fontAwesome, eIconType.url, eIconType.hosted] },
                iconColor: { type: "string" },
                autoSelect: { type: "boolean" },
                advancedTooltip: advancedTippyValidator,
                advancedPopup: advancedTippyValidator
            }
        }
    });
}
// ----------------------------------------------------------------------------------------------------------
export function SetToolbarIconsSchemeValidator(): JsonSchemaValidator {
    let itemInfo = {
        type: "array",
        items: {
            type: 'object',
            required: ["id"],
            properties: {
                // nested icons
                nestedItems: {
                    type: "array",
                    items: {
                        type: 'object',
                        required: ["id"],
                        properties: {
                            // BoardToolbarIcon properties
                            isSeparator: { type: "boolean" },
                            tooltip: { type: "string" },
                            id: { type: "string" },
                            icon: { type: "string" },
                            iconType: { type: "number", enum: [eIconType.fontAwesome, eIconType.url, eIconType.hosted] },
                            iconColor: { type: "string" },
                            autoSelect: { type: "boolean" }
                        }
                    }
                },
                // BoardToolbarIcon properties
                isSeparator: { type: "boolean" },
                tooltip: { type: "string" },
                id: { type: "string" },
                icon: { type: "string" },
                iconType: { type: "number", enum: [eIconType.fontAwesome, eIconType.url, eIconType.hosted] },
                iconColor: { type: "string" },
                autoSelect: { type: "boolean" },
                advancedTooltip: advancedTippyValidator,
                advancedPopup: advancedTippyValidator
            }
        }
    };
    return JsonSchemaValidator.GetInstance("SetToolbarIconsSchemeValidator", {
        type: "object",
        properties: {
            left: itemInfo,
            centre: itemInfo,
            right: itemInfo
        }
    });
}
// ------------------------------------------------------------------------------------------------
export function DrawerDataJsonSchemeValidator() {
    return JsonSchemaValidator
        .GetInstance("DrawerDataJsonSchemeValidator", {
            type: "object",
            properties: {
                position: { enum: ["bottom", "right", "left"] },
                size: { type: "number", minimum: 0 },
                pageId: { type: "string" },
                isModal: { type: "boolean" },
                isStatic: { type: "boolean" }
            },
            required: ["position", "size", "pageId", "isModal", "isStatic"]
        });
}
// ------------------------------------------------------------------------------------------------
export const enum CookiePortKeys {
    // Input
    Set = "Set",
    Delete = "Delete",
    Clear = "Clear",
    CookieOutput = "CookieOutput"
}
// ------------------------------------------------------------------------------------------------
export const enum GemDataKeys {
    ReceiveData = "ReceiveData",
    SendData = "SendData"
}
// ------------------------------------------------------------------------------------------------
export const enum BoardThingPagePortKeys {
    // Input
    HideWidgets = "HideWidgets",
    UnhideWidgets = "UnhideWidgets",

    // Output
    Init = "init",
}
export interface PopupPage {
    zIndex: number;
    pageId: string;

    width: number;
    height: number;
    isStatic: boolean;

    title: string;
    onCloseConfirm: boolean;
}
// ------------------------------------------------------------------------------------------------
export function PopupPageJsonSchemeValidator() {
    return JsonSchemaValidator
        .GetInstance("PopupPageJsonSchemeValidator", {
            type: "object",
            properties: {
                pageId: { type: "string" },
                width: { type: "number", minimum: 0, maximum: 1 },
                height: { type: "number", minimum: 0, maximum: 1 },
                isStatic: { type: "boolean" },
                title: { type: "string" },
                onCloseConfirm: { type: "boolean" }
            },
            required: ["pageId"]
        });
}

// ------------------------------------------------------------------------------------------------
interface PollingInfo {
    Key: string;
    ThingKey: string;
}
const __COOKIE_KEY__ = '__COOKIE_KEY__';
// ----------------------------------------------------------------------------------------------------------
function CookieJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("Cookie_JsonSchemeValidator", {
        title: "CookieJsonSchemeValidator",
        type: "array",
        items: {
            type: 'object',
            properties: {
                key: {
                    type: "string",
                },
                value: {
                    type: ["number", "integer", "string", "boolean", "array", "object", "null"]
                },
            },
            required: ["key", "value"]
        }
    });
}
// ----------------------------------------------------------------------------------------------------------
function GemsDataJsonSchemeValidator() {
    return JsonSchemaValidator.GetInstance("GemsDataJsonSchemeValidator", {
        title: "GemsDataJsonSchemeValidator",
        type: "object",
        properties: {
            topic: { type: "string" },
            data: { type: "object" }
        }
    });
}
// ---------------------------------------------------------------------------------------------------
export class BoardThings {
    // --------------------------------------------------------------------------------------------
    private static _instances: { [key: string]: BoardThings } = {};
    public static GetInstance(nodeKey: string): BoardThings { return (_.has(this._instances, nodeKey)) ? this._instances[nodeKey] : null; }
    // --------------------------------------------------------------------------------------------
    public NodeKey: string;
    public WebModuleKey: string;
    public MgmtThingKey: string;
    public CookieThingKey: string;
    public GemsDataThingKey: string;
    // --------------------------------------------------------------------------------------------
    public LocationPosition = {
        acc: 0,
        lat: 0,
        long: 0,
        status: LocationStatus.Disabled,
        errorMsg: undefined
    }
    // --------------------------------------------------------------------------------------------
    private ThingsInterface: BoardThingsInterface;
    private BoardActiveDashboardThingKey: string;
    private PagesActiveDashboardThingKeys: { [key: string]: string } = {};
    private PollingInfoKeys: { [key: string]: PollingInfo } = {};
    private InitializedDashboardPages: { [key: string]: boolean } = {};
    private DashboardInitEvent = false;
    private Pages: DashboardDescriptorDictionary = {};
    private WidgetsVisibility: { [key: string]: boolean } = {};
    getWidgetsVisibility = () => this.WidgetsVisibility;
    // --------------------------------------------------------------------------------------------
    private get Node() { return WebNode.GetInstanceKey(this.NodeKey) }
    public getPage(id: string) { return this.Pages[id] }
    public getPageById(id: string) {
        return _.find(this.Pages, x => x.Settings.PageId == id);
    }
    // --------------------------------------------------------------------------------------------
    constructor(
        nodeKey: string,
        webModuleKey: string,
        thingsInterface: BoardThingsInterface,
        pages: DashboardDescriptorDictionary,
        enableLocation: boolean) {

        // Check if we allready have init the nodekey
        if (_.has(BoardThings._instances, nodeKey))
            DebugEx.TraceWarning("Try to init board things again");

        // Link the board things
        BoardThings._instances[nodeKey] = this;
        this.WebModuleKey = webModuleKey;
        this.NodeKey = nodeKey;
        this.ThingsInterface = thingsInterface;
        this.Pages = pages;

        // Clear any old keys
        this.PagesActiveDashboardThingKeys = {};
        this.PollingInfoKeys = {};
        this.InitializedDashboardPages = {};
        this.WidgetsVisibility = {};

        // Init the things
        if (this.Node != null) {
            this._initMgmtThing();
            this._initActivePagesThing(pages);
            this._initPulseThing(pages);
            this._initCookieThing();
            this._initGemDataThing();
        }

        // Enable location service
        if (enableLocation) {
            if (isDefined(navigator.geolocation)) {
                this.LocationPosition.status = LocationStatus.Started;
                navigator.geolocation.watchPosition(
                    this.locationWatcher,
                    this.locationWatcherError,
                    { enableHighAccuracy: true });
            } else {
                this.LocationPosition.status = LocationStatus.NotSupported;
            }
        } else {
            this.LocationPosition = null;
        }
        this.locationUpdateNode();
        this.cookieUpdateNode();
    }
    // --------------------------------------------------------------------------------------------
    public locationWatcherError = (positionError: GeolocationPositionError) => {
        this.LocationPosition.status = LocationStatus.Error;
        this.LocationPosition.errorMsg = positionError.message;
        this.locationUpdateNode();
    }
    // --------------------------------------------------------------------------------------------
    public locationWatcher = (position: GeolocationPosition) => {
        // Stored the location and update it
        this.LocationPosition = {
            acc: position.coords.accuracy,
            lat: position.coords.latitude,
            long: position.coords.longitude,
            status: LocationStatus.Valid,
            errorMsg: undefined
        };
        this.locationUpdateNode();
    }
    // --------------------------------------------------------------------------------------------
    public locationUpdateNode() {
        var node = this.Node;
        if (node?.DummyNode) return;

        if (node != null) {
            if (this.LocationPosition != null)
                node.UpdateLocation(this.LocationPosition);
            else
                node.UpdateLocation(null);
        }
        this.ThingsInterface.refreshRequest();
    }
    // --------------------------------------------------------------------------------------------
    public cookieUpdateNode() {
        var node = this.Node;
        if (node?.DummyNode) return;

        if (node != null) {
            // get saved cookies from local storage
            let cookie: { [key: string]: any } = {};
            cookie = JSON.parse(localStorage.getItem(this.NodeKey + __COOKIE_KEY__));

            // Update the node
            node.UpdateCookie(cookie);
        }
        this.ThingsInterface.refreshRequest();
    }
    // --------------------------------------------------------------------------------------------
    public ReInit(pages: DashboardDescriptorDictionary) {
        // Clear any old keys
        this.PagesActiveDashboardThingKeys = {};
        this.PollingInfoKeys = {};
        this.InitializedDashboardPages = {};
        this.Pages = pages;

        // Init the things
        if (this.Node != null) {
            this._initMgmtThing();
            this._initActivePagesThing(pages);
            this._initPulseThing(pages);
            this._initCookieThing();
        }
    }
    // --------------------------------------------------------------------------------------------
    public ReInitPage(dashboards: DashboardDescriptorDictionary, page: DashboardDescriptor, sendUpdateToServer: boolean = true) {
        let webNode = this.Node;
        if (isDefined(webNode)) {
            if (isDefined(page)) {
                this._initPageThing(page, webNode);
                this._initPagePulseThing(page, webNode);
            }
            this._initBoardActiveDashboardThing(dashboards, webNode);

            // Send update to the server 
            if (sendUpdateToServer)
                webNode.SendUpdateToServer();
        }
    }
    // --------------------------------------------------------------------------------------------
    public SetThingsInterface(thingsInterface: BoardThingsInterface) {
        this.ThingsInterface = thingsInterface;
    }
    // --------------------------------------------------------------------------------------------
    public getPageThingKeys(pageWebModule: string): string[] {
        let keys = [];

        if (_.has(this.PagesActiveDashboardThingKeys, pageWebModule))
            keys.push(this.PagesActiveDashboardThingKeys[pageWebModule])

        if (_.has(this.PollingInfoKeys, pageWebModule))
            keys.push(this.PollingInfoKeys[pageWebModule].ThingKey);

        return keys;
    }
    // --------------------------------------------------------------------------------------------
    // Init Things
    // --------------------------------------------------------------------------------------------
    private _initMgmtThing() {
        // Find the webnode and the webmodule for the things
        let webNode = this.Node;
        let webModule = webNode?.GetModule(this.WebModuleKey);
        if (webModule != null) {
            // check is DashboardMgmt thing is already created
            let thing = webNode.GetThing(webModule.ModuleKey + "_DashboardMgmt");
            if (thing == null) {
                // Create thing.
                let yThing: Plegma.Thing = {
                    Name: "Dashboard",
                    ThingKey: "DashboardMgmt",
                    UIHints: {
                        Description: "Dashboard",
                        IconURI: IconURIPath + "/svg/dashboard2.svg"
                    }
                };

                // Create thing's ports.
                let ports: WebThingPort[] = [new WebThingPort(
                    {
                        Name: "Go To",
                        PortKey: BoardThingMgmtPortKeys.GoTo,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ""
                    },
                    WebThingPortType.Trigger,
                    () => { return { valid: true, errors: null }; },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.changeSelectedPage(p.State)
                    }
                ),
                new WebThingPort(
                    {
                        Name: "Active Page Key",
                        PortKey: BoardThingMgmtPortKeys.ActiveDashboardKey,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Output,
                        State: ""
                    },
                    WebThingPortType.Trigger
                ),
                new WebThingPort(
                    {
                        Name: "Init Dashboard",
                        PortKey: BoardThingMgmtPortKeys.InitDashboardKey,
                        Type: Plegma.ePortType.Boolean,
                        ioDirection: Plegma.ioPortDirection.Output,
                        State: "False"
                    },
                    WebThingPortType.Trigger
                ),
                new WebThingPort(
                    {
                        Name: "Open Page Dialog",
                        PortKey: BoardThingMgmtPortKeys.OpenPageDialog,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ""
                    },
                    WebThingPortType.Trigger,
                    data => {
                        // obj
                        if (isObject(data)) {
                            return PopupPageJsonSchemeValidator().DataValidateString(data);
                        }
                        // json 
                        else if (_.startsWith(data, '{') && _.endsWith(data, '}')) {
                            try {
                                const popupData = JSON.parse(data);
                                return PopupPageJsonSchemeValidator().DataValidateString(popupData);
                            }
                            catch (e) { return { valid: false, errors: e.stack }; }
                        }
                        // nullish 
                        else if (!data) {
                            return { valid: true, errors: null };
                        }
                        else {
                            return { valid: false, errors: 'Invalid type' };
                        }
                    },
                    (e, p) => {
                        if (p.LastStateStatus != StateStatus.Valid)
                            return;

                        let state: PopupPage = null;

                        // check if object
                        if (isObject(p.State)) {
                            state = p.State;
                        } else {
                            let stateString = _.trim(p.State);
                            if (_.startsWith(stateString, '{') && _.endsWith(stateString, '}')) {
                                // parse state
                                state = JSON.parse(stateString);
                            } else {
                                // create default popup page object
                                state = {
                                    zIndex: 0,
                                    height: 0.95,
                                    width: 0.95,
                                    pageId: stateString,
                                    isStatic: false,
                                    title: null,
                                    onCloseConfirm: false,
                                };
                            }
                        }

                        // fix width
                        if (!isDefined(state.width))
                            state.width = 0.95;
                        // fix height
                        if (!isDefined(state.height))
                            state.height = 0.95;

                        this.ThingsInterface.openDialogDashboard(state);
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Close Page Dialog',
                        PortKey: BoardThingMgmtPortKeys.ClosePageDialog,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    () => { return { valid: true, errors: null }; },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid) {
                            let state: number[] = null;
                            if (isObject(p.State)) {
                                state = p.State;
                            } else {
                                // parse state
                                try {
                                    let stateString = _.trim(p.State);
                                    if (_.startsWith(stateString, '[') && _.endsWith(stateString, ']'))
                                        state = JSON.parse(stateString);
                                }
                                catch (ex) { DebugEx.TraceError(ex); }
                            }
                            this.ThingsInterface.closeDialogDashboard(state)
                        }
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Hide Pages',
                        PortKey: BoardThingMgmtPortKeys.HidePage,
                        Type: Plegma.ePortType.String, // or json string
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    () => { return { valid: true, errors: null } },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.hidePages(parseStrIntoStrArr(p.State));
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Unhide Pages',
                        PortKey: BoardThingMgmtPortKeys.UnhidePage,
                        Type: Plegma.ePortType.String, // or json string
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    () => { return { valid: true, errors: null }; },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.unHidePages(parseStrIntoStrArr(p.State));
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Show Toast',
                        PortKey: 'ToastNotification',
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    data => ToastNotificationSchemeValidator().DataValidateString(data),
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            showToastNotification(p.State, (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip) => {
                                let thing = webNode.GetThing(webModule.ModuleKey + "_DashboardMgmt");
                                if (!isDefined(thing)) {
                                    DebugEx.TraceCritical("Could not detect Thing for key: " + webModule.ModuleKey + "_DashboardMgmt");
                                    return;
                                }

                                // send port Event
                                thing.SendPortEvent(
                                    BoardThingMgmtPortKeys.ToastNotificationFeedback,
                                    createPortStateFromInfoTipEvent(i, infoTip, false), null);
                            });
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'User event from Toast',
                        PortKey: BoardThingMgmtPortKeys.ToastNotificationFeedback,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Output,
                        State: ''
                    },
                    WebThingPortType.Trigger
                ),
                new WebThingPort(
                    {
                        Name: "Open Drawer Page",
                        PortKey: BoardThingMgmtPortKeys.OpenDrawerPage,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ""
                    },
                    WebThingPortType.Trigger,
                    drawerData => DrawerDataJsonSchemeValidator().DataValidateString(drawerData),
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.openDrawerPage(p.State);
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Close Drawer Page',
                        PortKey: BoardThingMgmtPortKeys.CloseDrawerPage,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    () => { return { valid: true, errors: null }; },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.closeDrawerPage(p.State);
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Select toolbar icons',
                        PortKey: BoardThingMgmtPortKeys.SelectToolbarIcons,
                        Type: Plegma.ePortType.String, // or json string
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: '[]'
                    },
                    WebThingPortType.Trigger,
                    () => { return { valid: true, errors: null }; },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.selectToolbarIcons(parseStrIntoStrArr(p.State))
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Toolbar icon selected',
                        PortKey: BoardThingMgmtPortKeys.ToolbarIconsSelected,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Output,
                        State: ''
                    },
                    WebThingPortType.Trigger
                ),
                new WebThingPort(
                    {
                        Name: 'Hide toolbar icons',
                        PortKey: BoardThingMgmtPortKeys.HideToolbarIcons,
                        Type: Plegma.ePortType.String, // or json string
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: '[]'
                    },
                    WebThingPortType.Trigger,
                    () => { return { valid: true, errors: null }; },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.hideToolbarIcons(parseStrIntoStrArr(p.State))
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Unhide toolbar icons',
                        PortKey: BoardThingMgmtPortKeys.UnhideToolbarIcons,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: '[]'
                    },
                    WebThingPortType.Trigger,
                    () => { return { valid: true, errors: null }; },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.unhideToolbarIcons(parseStrIntoStrArr(p.State))
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Add left toolbar icons',
                        PortKey: BoardThingMgmtPortKeys.AddLeftToolbarIcons,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: '[]'
                    },
                    WebThingPortType.Trigger,
                    data => ToolbarIconsSchemeValidator().DataValidateString(data),
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.addLeftToolbarIcons(p.State)
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Add right toolbar icons',
                        PortKey: BoardThingMgmtPortKeys.AddRightToolbarIcons,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: '[]'
                    },
                    WebThingPortType.Trigger,
                    data => ToolbarIconsSchemeValidator().DataValidateString(data),
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.addRightToolbarIcons(p.State)
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Set toolbar icons',
                        PortKey: BoardThingMgmtPortKeys.SetToolbarIcons,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: '{}'
                    },
                    WebThingPortType.Trigger,
                    data => SetToolbarIconsSchemeValidator().DataValidateString(data),
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            this.ThingsInterface.setToolbarIcons(p.State)
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Open URL',
                        PortKey: BoardThingMgmtPortKeys.OpenURL,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    data => {
                        //TODO: check if this is a valid html url
                        return _.isString(data)
                            ? { valid: true, errors: "" }
                            : { valid: false, errors: "Expected string" }
                    },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid)
                            window.open(e.State, "_blank").focus();
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Copy to clipboard',
                        PortKey: BoardThingMgmtPortKeys.CopyToClipboard,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    data => {
                        //TODO: check if this is a valid html url
                        return _.isString(data)
                            ? { valid: true, errors: "" }
                            : { valid: false, errors: "Expected string" }
                    },
                    async (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid) {
                            try {
                                await navigator.clipboard.writeText(e.State);
                            } catch (error) {
                                console.error(error.message);
                            }
                        }
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Redirect URL',
                        PortKey: BoardThingMgmtPortKeys.RedirectURL,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    data => {
                        //TODO: check if this is a valid html url
                        return _.isString(data)
                            ? { valid: true, errors: "" }
                            : { valid: false, errors: "Expected string" }
                    },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid) {
                            window.open(e.State, "_self").focus();
                        }
                    }
                ),
                new WebThingPort(
                    {
                        Name: 'Button pressed from advanced toolbar icon',
                        PortKey: BoardThingMgmtPortKeys.ButtonPressedFromAdvancedToolbarIcon,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Output,
                        State: ''
                    },
                    WebThingPortType.Trigger
                ),
                new WebThingPort(
                    {
                        Name: 'Element changed from advanced toolbar icon',
                        PortKey: BoardThingMgmtPortKeys.ElementChangedFromAdvancedToolbarIcon,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Output,
                        State: ''
                    },
                    WebThingPortType.Trigger
                ),
                new WebThingPort(
                    {
                        Name: 'Set board language',
                        PortKey: BoardThingMgmtPortKeys.SetBoardLanguage,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    (req: LocalizationChangeLanguageReq) => {
                        return _.isString(req?.Language)
                            ? { valid: true, errors: "" }
                            : { valid: false, errors: "Expected json object of format: {Language:string, ReloadBoard:boolean}" };
                    },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid) {
                            const payload = p.State as LocalizationChangeLanguageReq;
                            const localization = Localization.GetInstance(this.NodeKey);

                            localization?.ChangeSelectedLanguage(payload);
                            this.ThingsInterface.onLanguageChange(payload.Language);
                        }
                    }
                ),
                new WebThingPort({
                    Name: 'Board language selected',
                    PortKey: BoardThingMgmtPortKeys.BoardLanguageSelected,
                    Type: Plegma.ePortType.String,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: ''
                }, WebThingPortType.Trigger)];

                // Create web thing.
                let thing = new WebThing(webModule, yThing, ports);
                this.MgmtThingKey = thing.ThingKey;
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    private _initActivePagesThing(dashboards: DashboardDescriptorDictionary) {
        let webNode = this.Node;

        // Create in each webmodule of each page one active port event
        _.forOwn(dashboards, (d) => {
            this._initPageThing(d, webNode);
        });

        // Create a common active dashboard thing with multiple ports
        if (this.WebModuleKey != null) {
            this._initBoardActiveDashboardThing(dashboards, webNode);
        }
    }
    // --------------------------------------------------------------------------------------------
    public sendGemData(topic: string, data: any) {
        let thing = this.Node?.GetThing(this.GemsDataThingKey);
        thing?.SendPortEvent(GemDataKeys.SendData, { topic, data }, null);
    }
    // --------------------------------------------------------------------------------------------
    private _initGemDataThing() {
        // Find the webnode and the webmodule for the things
        let webNode = this.Node;
        let webModule = webNode?.GetModule(this.WebModuleKey);
        if (webModule != null) {
            // check if dashboard 
            let thing = webNode.GetThing(webModule.ModuleKey + "_GemData");
            if (thing == null) {
                // Create thing.
                let yThing: Plegma.Thing = {
                    Name: "Gem Data",
                    ThingKey: "GemData",
                    UIHints: {
                        Description: "Send data to gem data",
                        IconURI: IconURIPath + "/svg/gem.svg"
                    }
                };

                // Create thing's ports.
                let ports: WebThingPort[] = [new WebThingPort(
                    {
                        Name: "Data",
                        PortKey: GemDataKeys.ReceiveData,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    data => GemsDataJsonSchemeValidator().DataValidateString(data),
                    (e, p) => {
                        // Send data to board macro engine 
                        let boardMacroEngine = BoardMacroEngine.GetInstance(this.NodeKey);
                        if (isDefined(boardMacroEngine))
                            boardMacroEngine.setCyanRegister(p.State.topic, p.State.data);
                    }
                ),
                new WebThingPort(
                    {
                        Name: "Data",
                        PortKey: GemDataKeys.SendData,
                        Type: Plegma.ePortType.JsonString,
                        ioDirection: Plegma.ioPortDirection.Output,
                        State: ''
                    },
                    WebThingPortType.Trigger
                )];

                // Create web thing.
                let thing = new WebThing(webModule, yThing, ports);
                this.GemsDataThingKey = thing.ThingKey;
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    private _initCookieThing() {
        // Find the webnode and the webmodule for the things
        let webNode = this.Node;
        let webModule = webNode?.GetModule(this.WebModuleKey);
        if (webModule != null) {
            // check is DashboardMgmt thing is already created
            let thing = webNode.GetThing(webModule.ModuleKey + "_Cookie");
            if (thing == null) {
                // Create thing.
                let yThing: Plegma.Thing = {
                    Name: "Cookie",
                    ThingKey: "Cookie",
                    UIHints: {
                        Description: "Cookie",
                        IconURI: IconURIPath + "/svg/cookie.svg"
                    }
                };

                // Create thing's ports.
                let ports: WebThingPort[] = [
                    new WebThingPort(
                        {
                            Name: "Set",
                            PortKey: CookiePortKeys.Set,
                            Type: Plegma.ePortType.JsonString,
                            ioDirection: Plegma.ioPortDirection.Input,
                            State: ''
                        },
                        WebThingPortType.Trigger,
                        data => CookieJsonSchemeValidator().DataValidateString(data),
                        (e, p) => {
                            if (p.LastStateStatus == StateStatus.Valid)
                                this._setCookie(p)
                        }
                    ),
                    new WebThingPort(
                        {
                            Name: "Delete",
                            PortKey: CookiePortKeys.Delete,
                            Type: Plegma.ePortType.String,
                            ioDirection: Plegma.ioPortDirection.Input,
                            State: ''
                        },
                        WebThingPortType.Trigger,
                        () => { return { valid: true, errors: null } },
                        (e, p) => {
                            if (p.LastStateStatus == StateStatus.Valid)
                                this._deleteCookie(p.State)
                        }
                    ),
                    new WebThingPort(
                        {
                            Name: "Clear",
                            PortKey: CookiePortKeys.Clear,
                            Type: Plegma.ePortType.Boolean,
                            ioDirection: Plegma.ioPortDirection.Input,
                            State: ''
                        },
                        WebThingPortType.Trigger,
                        (data) => { return { valid: true, errors: null } },
                        (e, p) => {
                            if (p.LastStateStatus == StateStatus.Valid)
                                this._clearAllCookies()
                        }
                    ),
                ];

                // Create web thing.
                let thing = new WebThing(webModule, yThing, ports);
                this.CookieThingKey = thing.ThingKey;
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    private _setCookie = (port: WebThingPort) => {
        try {
            if (port.LastStateStatus == StateStatus.Valid) {
                let newCookie: { key: string, value: any }[] = port.State;
                // Check if there is any saved cookie
                let cookie: { [key: string]: any } = {};
                cookie = JSON.parse(localStorage.getItem(this.NodeKey + __COOKIE_KEY__));
                if (cookie == null)
                    cookie = {};

                // Add new cookies from port data
                _.map(newCookie, (k, i) => {
                    cookie[k.key] = k.value;
                });

                // Store new cookie in local storage.
                localStorage.setItem(
                    this.NodeKey + __COOKIE_KEY__,
                    JSON.stringify(cookie)
                );

                // update cookie's thing output port
                let boardThing = BoardThings.GetInstance(this.NodeKey);
                if (boardThing != null) {
                    boardThing.cookieUpdateNode();
                }
            }
        } catch (ex) {
            DebugEx.TraceError(ex);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private _deleteCookie = (dictKey: string) => {
        // Read local storage for columns visibility.
        let cookie: { [key: string]: any } = {};
        cookie = JSON.parse(localStorage.getItem(this.NodeKey + __COOKIE_KEY__));

        // Check if cookie contains that key and then remove it
        if (_.has(cookie, dictKey)) {
            delete cookie[dictKey];

            // Store updated cookie in local storage.
            localStorage.setItem(
                this.NodeKey + __COOKIE_KEY__,
                JSON.stringify(cookie)
            );

            let boardThing = BoardThings.GetInstance(this.NodeKey);
            if (boardThing != null) {
                // update cookie's thing output port
                boardThing.cookieUpdateNode();
            }
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private _clearAllCookies = () => {
        // Clear cookie from browser local storage
        localStorage.setItem(
            this.NodeKey + __COOKIE_KEY__,
            JSON.stringify({})
        );

        let boardThing = BoardThings.GetInstance(this.NodeKey);
        if (boardThing != null) {
            // update cookie's thing output port
            boardThing.cookieUpdateNode();
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private _initBoardActiveDashboardThing(dashboards: DashboardDescriptorDictionary, webNode: WebNode) {
        let webModule = webNode.GetModule(this.WebModuleKey);
        if (webModule != null) {
            // Ports
            let ports: WebThingPort[] = [];
            for (var key in dashboards) {
                // The name need to have the full path to be able to easier find it
                let settings = dashboards[key].Settings;
                let portName = settings.Path + PlegmaStatics.Constants.HierarchySeparator + settings.Name;
                portName = portName.replace("//", "/");
                if (portName.startsWith(PlegmaStatics.Constants.HierarchySeparator))
                    portName = portName.substring(1);
                ports.push(new WebThingPort({
                    Name: portName,
                    PortKey: dashboards[key].WebModuleKey,
                    Type: Plegma.ePortType.Boolean,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: "False"
                }, WebThingPortType.Trigger));
            }

            // before ActiveDashboard thing creation check if is already created then only update ports
            let thing = webNode.GetThing(webModule.ModuleKey + "_ActiveDashboard");
            if (thing == null) {
                // Thing description
                let plegmaThing: Plegma.Thing = {
                    Name: "Active Dashboard",
                    ThingKey: "ActiveDashboard",
                    UIHints: { Description: "ActiveDashboard", IconURI: IconURIPath + "/svg/dashboard.svg" },
                };
                // Create the thing
                thing = new WebThing(webModule, plegmaThing, ports);
            }
            else {
                // Clean old ports
                thing.Ports = {};
                // Add the ports
                ports.forEach(p => thing.AddPort(p, false));
                // Update thing in the webmodule
                webModule.UpdateThing(thing);
            }
            // Link the thing to this board
            this.BoardActiveDashboardThingKey = thing.ThingKey;
        }
    }
    // --------------------------------------------------------------------------------------------
    private _initPageThing(d: DashboardDescriptor, webNode: WebNode) {
        if (d.WebModuleKey != null) {
            let webModule = webNode.GetModule(d.WebModuleKey);
            if (webModule != null) {
                // The name need to have the full path to be able to easier find it
                let settings = d.Settings;
                let portName = settings.Path + PlegmaStatics.Constants.HierarchySeparator + settings.Name;
                portName = portName.replace("//", "/");
                if (portName.startsWith(PlegmaStatics.Constants.HierarchySeparator))
                    portName = portName.substring(1);
                portName += " (Loading)"

                let ports: WebThingPort[] = [new WebThingPort({
                    Name: portName,
                    PortKey: d.WebModuleKey,
                    Type: Plegma.ePortType.Boolean,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: "False"
                }, WebThingPortType.Trigger),
                new WebThingPort({
                    Name: "Init",
                    PortKey: BoardThingPagePortKeys.Init,
                    Type: Plegma.ePortType.Boolean,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: "False"
                }, WebThingPortType.Trigger),
                new WebThingPort(
                    {
                        Name: 'Hide Widgets',
                        PortKey: BoardThingPagePortKeys.HideWidgets,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    () => { return { valid: true, errors: null }; },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid) {
                            // Parse the incoming widget keys.
                            _.forEach(parseStrIntoStrArr(p.State), v => {

                                // Check that widget exists.
                                const widget = _.find(d.Widgets, w =>
                                    (w as IWidgetThingsClass<any>).NodeKey +
                                    (PlegmaStatics.PlegmaAPI.KeySeparator) +
                                    (w as IWidgetThingsClass<any>).ThingKey == v);

                                if (_.isEmpty(widget))
                                    return;

                                // Check that widget isn't hidden.
                                if (!widget.Description.IsHidden ||
                                    this.WidgetsVisibility[v] == true) {

                                    // Mark this widget as hidden.
                                    if (this.WidgetsVisibility[v] == true)
                                        delete this.WidgetsVisibility[v];
                                    else
                                        this.WidgetsVisibility[v] = false;
                                }
                            });

                            // Rerender page.
                            this.ThingsInterface.refreshRequest();
                        }
                    }),
                new WebThingPort(
                    {
                        Name: 'Unhide Widgets',
                        PortKey: BoardThingPagePortKeys.UnhideWidgets,
                        Type: Plegma.ePortType.String,
                        ioDirection: Plegma.ioPortDirection.Input,
                        State: ''
                    },
                    WebThingPortType.Trigger,
                    () => { return { valid: true, errors: null }; },
                    (e, p) => {
                        if (p.LastStateStatus == StateStatus.Valid) {
                            // Parse the incoming widget keys.
                            _.forEach(parseStrIntoStrArr(p.State), v => {

                                // Check that widget exists.
                                const widget = _.find(d.Widgets, w =>
                                    (w as IWidgetThingsClass<any>).NodeKey +
                                    (PlegmaStatics.PlegmaAPI.KeySeparator) +
                                    (w as IWidgetThingsClass<any>).ThingKey == v);

                                if (_.isEmpty(widget))
                                    return;

                                // Check that widget is hidden.
                                if (widget.Description.IsHidden ||
                                    this.WidgetsVisibility[v] == false) {

                                    // Mark this widget as visible.
                                    if (this.WidgetsVisibility[v] == false)
                                        delete this.WidgetsVisibility[v];
                                    else
                                        this.WidgetsVisibility[v] = true;
                                }
                            });
                        }
                        // Rerender page.
                        this.ThingsInterface.refreshRequest();
                    })
                ];
                // before ActiveDashboard thing creation check if is already created then only update ports
                let thing = webNode.GetThing(webModule.ModuleKey + "_ActiveDashboard");
                if (thing == null) {
                    // Thing description
                    let plegmaThing: Plegma.Thing = {
                        Name: "Active Dashboard",
                        ThingKey: "ActiveDashboard",
                        UIHints: { Description: "ActiveDashboard", IconURI: IconURIPath + "/svg/dashboard.svg" },
                    };
                    // Create the thing
                    thing = new WebThing(webModule, plegmaThing, ports);
                }
                else {
                    // Clean old ports
                    thing.Ports = {};
                    // Add the ports
                    ports.forEach(p => thing.AddPort(p, false));
                    // Update thing in the webmodule
                    webModule.UpdateThing(thing);
                }
                this.PagesActiveDashboardThingKeys[d.WebModuleKey] = thing.ThingKey;
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    private _initPulseThing(dashboards: DashboardDescriptorDictionary) {
        let webNode = this.Node;

        // Create in each webmodule of each page one active port event
        if (webNode != null)
            _.forOwn(dashboards, (d) => {
                this._initPagePulseThing(d, webNode);
            });
    }
    // --------------------------------------------------------------------------------------------
    private _initPagePulseThing(d: DashboardDescriptor, webNode: WebNode) {
        if (d.WebModuleKey != null) {
            let webModule = webNode.GetModule(d.WebModuleKey);
            if (webModule != null) {
                // The name need to have the full path to be able to easier find it
                let settings = d.Settings;
                let thingName = settings.Path + PlegmaStatics.Constants.HierarchySeparator + settings.Name;
                thingName = thingName.replace("//", "/");
                if (thingName.startsWith(PlegmaStatics.Constants.HierarchySeparator))
                    thingName = thingName.substring(1);
                let ports: WebThingPort[] = [
                    new WebThingPort({
                        Name: "Polling",
                        PortKey: "polling",
                        Type: Plegma.ePortType.Boolean,
                        ioDirection: Plegma.ioPortDirection.Output,
                        State: "False"
                    }, WebThingPortType.Trigger)
                ];
                // before ActiveDashboard thing creation check if is already created then only update ports
                let thing = webNode.GetThing(webModule.ModuleKey + "_Polling");
                if (thing == null) {
                    // Thing description
                    let plegmaThing: Plegma.Thing = {
                        Name: thingName + " - Polling",
                        ThingKey: "Polling",
                        UIHints: { Description: "Pulse", IconURI: IconURIPath + "/png/polling.png" },
                    };
                    // Create the thing
                    let thing = new WebThing(webModule, plegmaThing, ports);
                    this.PollingInfoKeys[d.WebModuleKey] = { ThingKey: thing.ThingKey, Key: d.Key };
                }
                else {
                    this.PollingInfoKeys[d.WebModuleKey] = { ThingKey: thing.ThingKey, Key: d.Key };
                    // Clean old ports
                    thing.Ports = {};
                    // Add the ports
                    ports.forEach(p => thing.AddPort(p, false));
                    // Update thing
                    webModule.UpdateThing(thing);
                }
            }
            else {
                console.error("<====");
            }
        }
        else {
            console.error("<====");
        }
    }
    // --------------------------------------------------------------------------------------------
    public UpdateActiveDashboardPortState(key: string) {
        let webNode = this.Node;

        // Send port event in the specific active port thing
        if (_.has(this.PagesActiveDashboardThingKeys, key)) {
            let activeDashboardThing = webNode.GetThing(this.PagesActiveDashboardThingKeys[key]);
            if (activeDashboardThing != null) {
                activeDashboardThing.SendPortEvent(key, true, null);

                // Check if this is the first time
                if (!yHas(this.InitializedDashboardPages, key)) {
                    activeDashboardThing.SendPortEvent(BoardThingPagePortKeys.Init, true, null);
                    this.InitializedDashboardPages[key] = true;
                }
            }
        }

        // Send port event in the generic thing
        if (this.WebModuleKey != null) {
            let webModule = webNode.GetModule(this.WebModuleKey);
            if (webModule != null) {
                let events: Plegma.PortEvent[] = [];
                // Accumulate all events in this webmodule
                _.forOwn(webModule.Things, (tvalue) => {
                    _.forOwn(tvalue.Ports, (pValue) => {
                        // include the port value only if is active
                        if (pValue.PlegmaPort.ioDirection == Plegma.ioPortDirection.Output ||
                            pValue.PlegmaPort.ioDirection == Plegma.ioPortDirection.InputOutput) {
                            if (pValue.PortType == WebThingPortType.Data) {
                                events.push({
                                    PortKey: tvalue.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + pValue.PortKey,
                                    State: pValue.State,
                                })
                            }
                        }
                    });
                });

                // search for right dashboard's port and update state
                let activeDashboardThing = webNode.GetThing(this.BoardActiveDashboardThingKey);
                if (activeDashboardThing != null)
                    _.forOwn(activeDashboardThing.Ports, element => {
                        if (element.PortKey == WebThingKeys.UserEmailKey) {
                            events.push({
                                PortKey: activeDashboardThing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + element.PortKey,
                                State: webNode.YodiWS.UserInfo.Email
                            });
                        } else if (element.PortKey == WebThingKeys.Location && element.State != null) {
                            events.push({
                                PortKey: activeDashboardThing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + element.PortKey,
                                State: "Location"
                            });
                        } else if (element.PortKey == WebThingKeys.Cookie && element.State != null) {
                            events.push({
                                PortKey: activeDashboardThing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + element.PortKey,
                                State: "Cookie"
                            });
                        } else if (element.PortKey == WebThingKeys.URLParamsKey) {
                            events.push({
                                PortKey: activeDashboardThing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + element.PortKey,
                                State: JSON.stringify(getUrlParameters())
                            });
                            // check if current loaded dashboard isn't null (first selection)
                        } else if (element.PortKey == WebThingKeys.BoardLanguageKey) {
                            let localization = Localization.GetInstance(this.NodeKey);
                            events.push({
                                PortKey: activeDashboardThing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + element.PortKey,
                                State: (localization != null) ? localization.SelectedLanguage : ""
                            });
                            // check if current loaded dashboard isn't null (first selection)
                        } else if (this.WebModuleKey == element.PortKey) {
                            events.push({
                                PortKey: activeDashboardThing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + element.PortKey,
                                State: "false",
                            });
                        } else if (key == element.PortKey) {
                            events.push({
                                PortKey: activeDashboardThing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + element.PortKey,
                                State: "true",
                            });
                        }
                    });

                // search for dashboard management thing port and update state
                let dashboardMgmtThing = webNode.GetThing(this.MgmtThingKey);
                if (dashboardMgmtThing != null) {
                    // Find active page 
                    let page = _.find(this.Pages, p => p.WebModuleKey == key);
                    if (isDefined(page))
                        events.push({
                            PortKey: dashboardMgmtThing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + BoardThingMgmtPortKeys.ActiveDashboardKey,
                            State: page.Settings.PageId,
                        });
                    else
                        events.push({
                            PortKey: dashboardMgmtThing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + BoardThingMgmtPortKeys.ActiveDashboardKey,
                            State: key,
                        });

                    if (this.DashboardInitEvent == false) {
                        this.DashboardInitEvent = true;
                        events.push({
                            PortKey: dashboardMgmtThing.ThingKey + PlegmaStatics.PlegmaAPI.KeySeparator + BoardThingMgmtPortKeys.InitDashboardKey,
                            State: "True",
                        });
                    }
                }
                webNode.SendPortEventsPlegma(events);
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    public SendToolbarIconSelectedPortEvent(id: string) {
        WebNode
            .GetInstanceKey(this.NodeKey)
            .GetThing(this.MgmtThingKey)
            .SendPortEvent(BoardThingMgmtPortKeys.ToolbarIconsSelected, id, null);
    }
    // --------------------------------------------------------------------------------------------
    sendButtonPressedFromAdvancedToolbarIconPortEvent = (payload: IButtonPressedFromAdvancedToolbarIcon) => {
        WebNode
            .GetInstanceKey(this.NodeKey)
            .GetThing(this.MgmtThingKey)
            .SendPortEvent(BoardThingMgmtPortKeys.ButtonPressedFromAdvancedToolbarIcon, payload, null);
    }
    // --------------------------------------------------------------------------------------------
    sendElementChangedFromAdvancedToolbarIconPortEvent = (payload: IElementChangedFromAdvancedToolbarIcon) => {
        WebNode
            .GetInstanceKey(this.NodeKey)
            .GetThing(this.MgmtThingKey)
            .SendPortEvent(BoardThingMgmtPortKeys.ElementChangedFromAdvancedToolbarIcon, payload, null);
    }
    // --------------------------------------------------------------------------------------------
    sendLocalizationBoardLanguageSelectedPortEvent = (payload: string) => {
        WebNode
            .GetInstanceKey(this.NodeKey)
            .GetThing(this.MgmtThingKey)
            .SendPortEvent(BoardThingMgmtPortKeys.BoardLanguageSelected, payload, null);
    }

    // ======================================================================================================
    // Pulse
    // ======================================================================================================
    private _onPulse = (webModuleKey: string) => {
        let p = this.PollingInfoKeys[webModuleKey];

        if (!isDefined(p)) {
            console.error('Should not be here');
            return;
        }

        let webNode = WebNode.GetInstanceKey(this.NodeKey);
        let thing = webNode.GetThing(p.ThingKey);
        if (thing != null) {
            thing.SendPortEvent("polling", true, null);
        }
    };
    // ------------------------------------------------------------------------------------------------------
    public PulseThingStart(dashboard: DashboardDescriptor) {
        // Start if polling is enabled
        if (dashboard?.Settings.Polling.Enabled &&
            _.has(this.PollingInfoKeys, dashboard.WebModuleKey)) {
            Pulse.Start(dashboard, this._onPulse);
        }
    }
    // ------------------------------------------------------------------------------------------------------
    public PulseThingStop(dashboard: DashboardDescriptor) {
        Pulse.Stop(dashboard);
    }
    // ------------------------------------------------------------------------------------------------------
    public PulseThingStopAll() {
        Pulse.StopAll();
    }
    // ------------------------------------------------------------------------------------------------------
}

// ================================================================================================
/**
 * Wrapper class for handling multiple thing pulses.
 * This class caches instances. Foreach instance 1 timer for pulse is used.
 * Also this class hooks to 1 common focus handler.
 */
class Pulse {
    // ------------------------------------------------------------------------------------------------------
    private static _Instances: { [key: string]: Pulse } = {};
    private static _FocusId: number = null;
    private static _EnabledPulses: number = 0;
    // ------------------------------------------------------------------------------------------------------
    private static _HandlePulseStatusChange(enabled: boolean) {
        if (enabled) {
            Pulse._EnabledPulses++;
            // Init focus if needed
            if (!isDefined(Pulse._FocusId)) {
                Pulse._FocusId = addDocumentFocus((isVisible) => {
                    // trigger each instance
                    _.forEach(Pulse._Instances, (instance) => {
                        instance._pulseFocusUpdate(isVisible);
                    })
                });
            }
        }
        else {
            if (Pulse._EnabledPulses > 0)
                Pulse._EnabledPulses--;

            // disable focus handler if needed
            if (Pulse._EnabledPulses == 0 && isDefined(Pulse._FocusId)) {
                removeDocumentFocus(Pulse._FocusId);
                Pulse._FocusId = null;
            }
        }
    }
    // ------------------------------------------------------------------------------------------------------
    public static Start(
        dashboard: DashboardDescriptor,
        onPulse: (webModuleKey: string) => void
    ) {

        // try get cached instance
        let instance = Pulse._Instances[dashboard.Key]
        if (!isDefined(instance))
            // create new instance
            instance = Pulse._Instances[dashboard.Key] = new Pulse(dashboard, onPulse);
        else {
            // update instance
            instance.dashboard = dashboard;
        }
        instance._start();
    }
    // ------------------------------------------------------------------------------------------------------
    public static Stop(dashboard: DashboardDescriptor) {
        const instance = Pulse._Instances[dashboard?.Key];
        if (!isDefined(instance)) return;
        Pulse._StopInstance(instance);
    }
    // ------------------------------------------------------------------------------------------------------
    public static StopAll() {
        _.forEach(Pulse._Instances, (instance) => {
            Pulse._StopInstance(instance);
        });
    }
    // ------------------------------------------------------------------------------------------------------
    /**
     * Stop pulse & remove instance from cache
     */
    private static _StopInstance(instance: Pulse) {
        instance._stop();
        delete Pulse._Instances[instance.dashboard.Key];
    }
    // ------------------------------------------------------------------------------------------------------
    private _pulseTimer: number = null;
    private get _pulseEnabled(): boolean { return isDefined(this._pulseTimer); }
    // ------------------------------------------------------------------------------------------------------
    private dashboard: DashboardDescriptor;
    private get Polling(): PollingSettings { return this.dashboard.Settings.Polling; }
    public onPulse: (webModuleKey: string) => void;
    // ------------------------------------------------------------------------------------------------------
    private constructor(dashboard: DashboardDescriptor, onPulse: (webModuleKey: string) => void) {
        this.dashboard = dashboard;
        this.onPulse = onPulse;
    }
    // ------------------------------------------------------------------------------------------------------
    private _pulseFocusUpdate(isVisible: boolean) {
        let polling = this.Polling;
        if (!polling.Enabled ||
            !polling.EnabledOnlyInFocus)
            return;

        // Found if is active
        if (isVisible) {
            if (!this._pulseEnabled) {
                // Send a pulse in refocus
                if (polling.SendPollInRefocus)
                    this._handlePulse();

                // Start timer
                this._pulseTimer = window.setInterval(() => this._handlePulse(), polling.Period * polling.PeriodScale);
            }
        } else {
            this._stopPulse();
        }
    }
    // ------------------------------------------------------------------------------------------------------
    private _start() {

        // stop if needed
        this._stop();

        // check polling settings
        if (!this.dashboard.Settings.Polling.Enabled) {
            console.error("Timer without correct settings.", this.dashboard);
            return;
        }

        // enable pulse
        const polling = this.Polling;
        this._pulseTimer = window.setInterval(() => this._handlePulse(), polling.Period * polling.PeriodScale);

        // notify 
        Pulse._HandlePulseStatusChange(true);
    }
    // ------------------------------------------------------------------------------------------------------
    private _stop() {
        Pulse._HandlePulseStatusChange(false);

        // Stop Old pulses
        this._stopPulse();
    }
    // ------------------------------------------------------------------------------------------------------
    private _handlePulse() {
        if (!this.dashboard.Settings.Polling.Enabled) {
            console.error("Timer without correct settings.", this.dashboard);
            this._stopPulse();
            return;
        }
        RunSafe(() => this.onPulse(this.dashboard.WebModuleKey));
    }
    // ------------------------------------------------------------------------------------------------------
    private _stopPulse() {
        // Stop
        if (this._pulseEnabled)
            clearInterval(this._pulseTimer);
        this._pulseTimer = null;
    }
    // ------------------------------------------------------------------------------------------------------
}
// ================================================================================================